/**
 * useQuickReplies Hook
 *
 * Fetches predefined quick reply / canned response templates from message_templates table
 * These appear as clickable buttons in the chat interface for fast responses
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getErrorMessage } from '@/lib/error-utils';

export interface QuickReply {
  id: string;
  template_name: string;
  content: string;
  category: string;
  usage_count: number;
}

export function useQuickReplies(tenantId?: string) {
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tenantId) {
      fetchQuickReplies();
    }
  }, [tenantId]);

  const fetchQuickReplies = async () => {
    if (!tenantId) return;

    setLoading(true);
    setError(null);

    try {
      // Get tenant schema name
      const { data: tenant, error: tenantError } = await supabase
        .from('tenants')
        .select('schema_name')
        .eq('id', tenantId)
        .single();

      if (tenantError || !tenant) {
        throw new Error('Tenant not found');
      }

      // Fetch quick reply templates via typed RPC (active-only).
      const { data, error: fetchError } = await supabase.rpc('list_quick_replies', {
        p_tenant_schema: tenant.schema_name,
        p_include_inactive: false,
      });

      if (fetchError) {
        throw fetchError;
      }

      setQuickReplies(data || []);
    } catch (err: unknown) {
      console.error('Error fetching quick replies:', err);
      setError(getErrorMessage(err) || 'Failed to fetch quick replies');
      setQuickReplies([]);
    } finally {
      setLoading(false);
    }
  };

  const incrementUsageCount = async (templateId: string) => {
    if (!tenantId) return;

    try {
      const { data: tenant } = await supabase
        .from('tenants')
        .select('schema_name')
        .eq('id', tenantId)
        .single();

      if (!tenant) return;

      // Increment usage count via typed RPC.
      await supabase.rpc('increment_message_template_usage', {
        p_tenant_schema: tenant.schema_name,
        p_id: templateId,
      });

      // Refresh the list to show updated counts
      fetchQuickReplies();
    } catch (err) {
      console.error('Error incrementing usage count:', err);
    }
  };

  return {
    quickReplies,
    loading,
    error,
    refetch: fetchQuickReplies,
    incrementUsageCount,
  };
}
